import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

export default function ConfirmProvider({ children }) {
  const [prompt, setPrompt] = useState(null); // { title, message, confirmText, cancelText, tone, resolve }
  const lastFocusRef = useRef(null);
  
  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    return new Promise((resolve) => {
      lastFocusRef.current = document.activeElement;
      setPrompt({
        title: opts.title || 'Please Confirm',
        message: opts.message || 'Are you sure?',
        confirmText: opts.confirmText || 'Confirm',
        cancelText: opts.cancelText || 'Cancel',
        tone: opts.tone || 'default', // 'danger' | 'default'
        resolve,
      });
    });
  }, []);

  const close = useCallback((result) => {
    setPrompt((p) => {
      if (p && typeof p.resolve === 'function') {
        try { p.resolve(!!result); } catch {}
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!prompt) {
      // Restore focus when closed
      const el = lastFocusRef.current;
      if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 0);
      return;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(false); }
      if (e.key === 'Enter') {
        // Only trigger when focus is not inside a textarea/input with modifiers
        if (!(e.target && (e.target.tagName === 'TEXTAREA'))) {
          e.preventDefault(); close(true);
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [prompt, close]);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {prompt && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={() => close(false)} />
          <div className="relative w-full max-w-sm bg-neutral-900 rounded-xl shadow-xl shadow-black/40 border border-neutral-800 p-5 animate-[fadeIn_.15s_ease]">
            <h3 className="text-base font-semibold text-slate-100 mb-2">{prompt.title}</h3>
            <p className="text-sm text-slate-400 mb-4 whitespace-pre-wrap">{prompt.message}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="px-3 py-1.5 rounded-lg border border-neutral-700 text-sm text-slate-300 hover:bg-neutral-800 hover:text-slate-100"
              >{prompt.cancelText}</button>
              <button
                type="button"
                onClick={() => close(true)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white ${prompt.tone==='danger' ? 'bg-red-600 hover:bg-red-500 shadow shadow-red-600/30' : 'bg-emerald-600 hover:bg-emerald-500 shadow shadow-emerald-600/30'}`}
              >{prompt.confirmText}</button>
            </div>
          </div>
          <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(2px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
